/**
 * Analytics Event Constants
 *
 * Consolidated event name constants for the analytics feature.
 * Provides a unified structure for all analytics events with type safety.
 * @module features/analytics/constants
 */

/**
 * Unified analytics event name constants.
 *
 * Organized by event category:
 * - CORE: Fundamental app events (screen views)
 * - USER: User lifecycle and activity events
 * - NOTE: Note-related events
 * - PROFILER: React Profiler performance events
 * @example
 * ```typescript
 * import { ANALYTICS_EVENTS } from '@/features/analytics';
 *
 * // Log a screen view
 * logEvent(ANALYTICS_EVENTS.CORE.SCREEN_VIEW, { screen_name: 'Home' });
 *
 * // Log a user sign in
 * logEvent(ANALYTICS_EVENTS.USER.SIGN_IN, { method: 'phone' });
 * ```
 */
export const ANALYTICS_EVENTS = Object.freeze({
  /**
   * Core application events.
   * Fundamental events for app-wide tracking.
   */
  CORE: Object.freeze({
    /** Screen view navigation event */
    SCREEN_VIEW: "screen_view",
    /** Unknown/unmatched route reached the branded not-found screen */
    NOT_FOUND: "page_not_found",
  }),

  /**
   * User lifecycle and activity events.
   * Tracks user authentication, organization membership, and watchlist actions.
   */
  USER: Object.freeze({
    /** User sign-in event */
    SIGN_IN: "user_sign_in",
    /** User sign-out event */
    SIGN_OUT: "user_sign_out",
    /** User joined an organization event */
    ORGANIZATION_JOINED: "organization_joined",
    /** User left an organization event */
    ORGANIZATION_LEFT: "organization_left",
    /** Player added to watchlist event */
    PLAYER_WATCHLIST_ADDED: "player_watchlist_added",
    /** Player removed from watchlist event */
    PLAYER_WATCHLIST_REMOVED: "player_watchlist_removed",
    /** Watchlist created event */
    WATCHLIST_CREATED: "watchlist_created",
    /** Watchlist deleted event */
    WATCHLIST_DELETED: "watchlist_deleted",
    /** Watchlist updated event */
    WATCHLIST_UPDATED: "watchlist_updated",
    /** Active team switched event */
    TEAM_SWITCHED: "team_switched",
    /** Organization created event (SE-4010) */
    ORGANIZATION_CREATED: "organization_created",
    /** Organization updated event (SE-4010) */
    ORGANIZATION_UPDATED: "organization_updated",
    /** User invited to organization event (SE-4010) */
    INVITED: "user_invited",
  }),

  /**
   * Note-related events.
   * Tracks note creation and deletion.
   */
  NOTE: Object.freeze({
    /** Note created event */
    CREATED: "note_created",
    /** Note deleted event */
    DELETED: "note_deleted",
  }),

  /**
   * React Profiler performance events.
   * Captures render performance metrics from React's Profiler API.
   * @see {@link https://react.dev/reference/react/Profiler | React Profiler API}
   */
  PROFILER: Object.freeze({
    /** Profiler render performance event */
    RENDER: "profiler_render",
  }),

  /**
   * Player-centric feature events (SE-4010).
   * Tracks engagement with the player detail, comparison, and
   * similar-players surfaces — each carries the subject `player_id`,
   * which the generic `screen_view` event cannot express.
   */
  PLAYER: Object.freeze({
    /** Player profile (detail page) viewed event */
    PROFILE_VIEWED: "player_profile_viewed",
    /** Player comparison screen viewed event */
    COMPARISON_VIEWED: "player_comparison_viewed",
    /** Similar-players screen viewed event */
    SIMILAR_PLAYERS_VIEWED: "similar_players_viewed",
  }),

  /**
   * Player discovery events (SE-4010).
   * Tracks the player-search and player-filter surfaces.
   */
  SEARCH: Object.freeze({
    /** Player search executed (debounced non-empty query) event */
    PERFORMED: "search_performed",
    /** Search filter value applied event */
    FILTER_APPLIED: "filter_applied",
  }),

  /**
   * Scouting workflow events (SE-4010).
   * Tracks the kanban pipeline, match intake, scout reports,
   * shadow team, and knowledge-graph (Ask Gemini) surfaces.
   */
  SCOUTING: Object.freeze({
    /** Kanban board activated/viewed event */
    KANBAN_BOARD_VIEWED: "kanban_board_viewed",
    /** Match intake form opened event */
    MATCH_INTAKE_STARTED: "match_intake_started",
    /** Scout report opened (detail modal) event */
    REPORT_OPENED: "report_opened",
    /** Shadow team pitch viewed event */
    SHADOW_TEAM_VIEWED: "shadow_team_viewed",
    /** Knowledge-graph (Ask Gemini) screen opened event */
    KNOWLEDGE_GRAPH_OPENED: "knowledge_graph_opened",
  }),

  /**
   * Engagement-surface view events (SE-4010).
   * Tracks the home dashboard, activity feed, watchlist, roster, and
   * recommendations surfaces with feature params (layout, filter,
   * category) that route-level screen_view cannot express.
   */
  SURFACE: Object.freeze({
    /** Home (dashboard successor) screen viewed event */
    HOME_VIEWED: "home_viewed",
    /** Activity/update feed rendered event */
    ACTIVITY_FEED_VIEWED: "activity_feed_viewed",
    /** Watchlist screen viewed event */
    WATCHLIST_VIEWED: "watchlist_viewed",
    /** Roster management screen viewed event */
    ROSTER_VIEWED: "roster_viewed",
    /** Recommendations list viewed event */
    RECOMMENDATIONS_VIEWED: "recommendations_viewed",
  }),

  /**
   * Recruitment-pipeline events (SE-5508).
   *
   * Instruments the SE-6071 pipeline epic end to end: lifecycle (create /
   * stage-edit / card add / move / remove), the Suggested-players surface,
   * automation rules, the Review queue, the view toggle, first-card-visible
   * load time, and the Manager+ tier gate.
   *
   * These names are the ONLY place a pipeline event name is written. Every
   * emitter routes through `AnalyticsTracker.pipeline.*`, so a typo is a
   * compile error rather than a phantom event in PostHog.
   * @remarks
   * Every event here is a genuine user interaction that ORIGINATES in the
   * client, which is what makes PostHog the right home for it. `rule_executed`
   * is not one: a rule firing is a server-side occurrence, so a client event
   * would count observations rather than firings — over-counting when several
   * users open the same board and under-counting the firings nobody opens the
   * board to see. It is therefore not a pipeline event at all; the metric is
   * derived by querying `pipelineAutomationFirings` (SE-6259).
   */
  PIPELINE: Object.freeze({
    /** A pipeline was created (template or custom). */
    CREATED: "pipeline_created",
    /** A stage was added, renamed, reordered, deleted, or had a flag changed. */
    STAGE_EDITED: "pipeline_stage_edited",
    /** A player card entered the pipeline. */
    CARD_ADDED: "card_added",
    /** A card changed stage (drag or the drawer's stage picker). */
    CARD_MOVED: "card_moved",
    /** A card was removed from the pipeline. */
    CARD_REMOVED: "card_removed",
    /** Add or Dismiss pressed on a suggested candidate. */
    SUGGESTION_ACTED: "suggestion_acted",
    /** An automation rule (or guard) was created. */
    RULE_CREATED: "rule_created",
    /** A Review-queue item was accepted or declined. */
    REVIEW_ITEM_RESOLVED: "review_item_resolved",
    /** The Kanban/Table view toggle changed the mounted renderer. */
    VIEW_TOGGLED: "view_toggled",
    /** Time from board mount to first card visible, in milliseconds. */
    LOAD_TIME_MS: "pipeline_load_time_ms",
    /** A below-Manager (Scout tier) user was denied a pipeline surface. */
    TIER_BLOCK_RENDER: "tier_block_render",
  }),

  /**
   * Notification events (SE-4010).
   * Tracks push-notification engagement and preference changes.
   */
  NOTIFICATION: Object.freeze({
    /** Push notification tapped (opened) event */
    TAPPED: "notification_tapped",
    /** Notification preference toggled event */
    PREFERENCE_UPDATED: "notification_preference_updated",
  }),
});

// =============================================================================
// Backward Compatibility Exports
// =============================================================================

/**
 * Core events constant (backward compatibility).
 * Use ANALYTICS_EVENTS.CORE for new code.
 */
export const CORE_EVENTS = Object.freeze({
  SCREEN_VIEW: ANALYTICS_EVENTS.CORE.SCREEN_VIEW,
  NOT_FOUND: ANALYTICS_EVENTS.CORE.NOT_FOUND,
});

/**
 * User events constant (backward compatibility).
 * Use ANALYTICS_EVENTS.USER for new code.
 */
export const USER_EVENTS = Object.freeze({
  USER_SIGN_IN: ANALYTICS_EVENTS.USER.SIGN_IN,
  USER_SIGN_OUT: ANALYTICS_EVENTS.USER.SIGN_OUT,
  ORGANIZATION_JOINED: ANALYTICS_EVENTS.USER.ORGANIZATION_JOINED,
  ORGANIZATION_LEFT: ANALYTICS_EVENTS.USER.ORGANIZATION_LEFT,
  PLAYER_WATCHLIST_ADDED: ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_ADDED,
  PLAYER_WATCHLIST_REMOVED: ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_REMOVED,
  WATCHLIST_CREATED: ANALYTICS_EVENTS.USER.WATCHLIST_CREATED,
  WATCHLIST_DELETED: ANALYTICS_EVENTS.USER.WATCHLIST_DELETED,
  WATCHLIST_UPDATED: ANALYTICS_EVENTS.USER.WATCHLIST_UPDATED,
  TEAM_SWITCHED: ANALYTICS_EVENTS.USER.TEAM_SWITCHED,
  ORGANIZATION_CREATED: ANALYTICS_EVENTS.USER.ORGANIZATION_CREATED,
  ORGANIZATION_UPDATED: ANALYTICS_EVENTS.USER.ORGANIZATION_UPDATED,
  USER_INVITED: ANALYTICS_EVENTS.USER.INVITED,
});

/**
 * Note events constant (backward compatibility).
 * Use ANALYTICS_EVENTS.NOTE for new code.
 */
export const NOTE_EVENTS = Object.freeze({
  NOTE_CREATED: ANALYTICS_EVENTS.NOTE.CREATED,
  NOTE_DELETED: ANALYTICS_EVENTS.NOTE.DELETED,
});

/**
 * Profiler events constant (backward compatibility).
 * Use ANALYTICS_EVENTS.PROFILER for new code.
 * @see {@link https://react.dev/reference/react/Profiler | React Profiler API}
 */
export const PROFILER_EVENTS = Object.freeze({
  PROFILER_RENDER: ANALYTICS_EVENTS.PROFILER.RENDER,
});
